import { useEffect, useRef, createContext, useContext, type ReactElement } from 'react';
import wetvKernel from '@tencent/wetv-kernel';

import { REPORT_SDK_URL } from './constants';
import './types'; // 确保类型定义被加载

export interface ReportProviderProps {
  /** 子组件 */
  children: ReactElement<any>;
  /** 页面 ID（从外部传入，如 Layout 层） */
  pgid?: string;
  /** 上报参数（显式传递，会转换为 dt-params 传递给子组件） */
  params?: Record<string, any>;
  /** 页面容器 ID，默认为 PAGE_CONTAINER_ID */
  containerId?: string;
  /** 是否暂停上报，等待 dtPageHold 事件触发后再上报 */
  hold?: boolean;
  /** 上报初始化配置 */
  initCfg?: Partial<Record<string, any>>;
  /** 额外的上报参数 */
  otherAtta?: Record<string, any>;
  /** 是否针对异常上报元素进行染色 */
  errorEleDyeing?: boolean;
  /** 其他自定义属性，会透传给子组件 */
  [key: string]: any;
}

/** 上报上下文 */
interface ReportContextValue {
  /** 标准化后的 pgid（带 p_ 前缀） */
  formattedPgid: string;
  /** 原始 pgid（不带前缀） */
  rawPgid: string;
}

const ReportContext = createContext<ReportContextValue | null>(null);

/**
 * 获取上报上下文
 * @returns 包含标准化 pgid 的上下文对象
 */
export function useReportContext() {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error('useReportContext must be used within ReportProvider');
  }
  return context;
}

/**
 * 上报 Provider 组件
 * 集成了 SDK 动态加载、上报初始化、路由监听、页面切换等完整逻辑
 * 完全参考 wetv_static_cos_page 的 WetvReport 组件实现
 */
export function ReportProvider(props: ReportProviderProps) {
  const { children, pgid = '', params, initCfg = {}, otherAtta, errorEleDyeing, ...restProps } = props;

  // 使用 ref 保持最新的 otherAtta 值
  const otherAttaRef = useRef(otherAtta);
  otherAttaRef.current = otherAtta;

  // ⚠️ 关键：使用 ref 防止 SDK 重复初始化（解决 React 18 Strict Mode 双重调用问题）
  const isSDKInitialized = useRef(false);

  // SDK 动态加载（只执行一次）
  useEffect(() => {
    // ⚠️ 关键：防止重复初始化（React 18 Strict Mode 会双重调用 useEffect）
    if (isSDKInitialized.current) {
      console.log('WeTV Report SDK 已初始化，跳过重复初始化');
      return;
    }

    // ⚠️ 关键：检查 SDK 是否已存在
    if (typeof window.wetvReport === 'function' && window.wetvReportInstance) {
      console.log('WeTV Report SDK 已存在，跳过加载');
      isSDKInitialized.current = true;
      return;
    }

    const { fetcher } = wetvKernel;

    if (!fetcher || typeof fetcher.scriptFetch !== 'function') {
      return;
    }

    //  关键：标记为正在初始化
    isSDKInitialized.current = true;

    fetcher
      .scriptFetch({
        url: REPORT_SDK_URL,
        instanceName: 'wetvReport',
      })
      .then(() => {
        try {
          // 检查 window.wetvReport 是否可用
          if (typeof window.wetvReport !== 'function') {
            isSDKInitialized.current = false; // 加载失败，重置标志
            return;
          }

          // 关键：再次检查是否已经初始化（防止并发）
          if (window.wetvReportInstance) {
            return;
          }

          // 上报初始化
          window.wetvReport({
            routerType: 'path',
            externalCommonParams: {
              app_type: 'overseasLinuxTV',
            },
            isEnterEvent: (e: any) => e.code === 'Enter',
            getEnterEventTarget: (e: any) =>
              document.querySelector('[data-focused=true]') || document.activeElement || e.target,
            // 测试环境下染色错误上报元素
            errorEleDyeing: errorEleDyeing !== undefined ? errorEleDyeing : wetvKernel.configInstace?.cgiEnv === 'test',
            ...initCfg,
          });
        } catch (error) {
          console.error('WeTV Report SDK 初始化失败:', error);
          isSDKInitialized.current = false; // 初始化失败，重置标志
        }
      })
      .catch((error) => {
        console.error('WeTV Report SDK 加载失败:', error);
        isSDKInitialized.current = false; // 加载失败，重置标志
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只在组件挂载时执行一次

  // 标准化 pgid：添加 p_ 前缀
  const formattedPgid = pgid ? `p_linux_${pgid}` : '';

  // 将 params 对象转换为查询字符串格式（key=value&key2=value2）
  const formattedParams = params
    ? Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        .join('&')
    : undefined;

  // 针对频道页，提取 channel_id 作为唯一标识（用于自动触发重新曝光）
  const reuseIdentifier = pgid === 'channel' && params?.channel_id ? 'channel_id' : undefined;

  const contextValue: ReportContextValue = {
    formattedPgid,
    rawPgid: pgid,
  };
  // 使用 children.type 重新创建子组件，并传递所有属性（包括 dt-pgid、dt-params、dt-reuse-identifier 和 restProps）
  return (
    <ReportContext.Provider value={contextValue}>
      <children.type
        {...children.props}
        dt-pgid={formattedPgid}
        dt-params={formattedParams}
        dt-reuse-identifier={reuseIdentifier}
        {...restProps}
      />
    </ReportContext.Provider>
  );
}

// 重新导出其他需要的类型和 hooks
export { useRouteReport } from './useRouteReport';
export type { OtherAtta, ReportConfig, WetvReportInstance, ReportContextValue } from './types';
